/**
 * Phase II · M-P5 — Terminal rendering for Surface 3 (CLI Intent capture).
 *
 * Pure, side-effect-free render helpers. The CLI action layer calls these to
 * build strings it then prints — keeping the module testable without stubbing
 * `process.stdout` or picocolors.
 *
 * Style budget comes from the M-P0 design spec:
 *   - Agent-colored `→ Agent · Role` routing line (ANSI 256 per-agent)
 *   - Mono confidence decimal (never a bucket word)
 *   - Box-drawn plan preview — no ASCII art, no drop-shadow-via-shading
 *   - `>>` hotkey bar with single-letter verbs `[A]pprove [E]dit [X]archive [Q]uit`
 *   - Respect NO_COLOR (fall back to plain ASCII) + CI / non-TTY (skip spinners)
 */

import {
  AGENT_ANSI_256,
  AGENT_ROLES,
  CONFIDENCE_DECIMAL,
  type AgentSlug,
  type IntentPlanPreview,
} from "./intents.js";

export interface RenderOptions {
  /** Disable ANSI color output. Derived from NO_COLOR env by default. */
  noColor?: boolean;
  /**
   * Disable unicode box-drawing / glyphs. Falls back to ASCII equivalents —
   * used when the locale cannot represent the characters. Distinct from
   * `noColor`: a truecolor terminal in a plain LC_ALL=C locale wants color on,
   * unicode off.
   */
  asciiOnly?: boolean;
}

/**
 * Cheap env snapshot so CLI action code can pass the same options to every
 * render helper without repeatedly consulting process.env.
 */
export function detectRenderCapabilities(
  env: NodeJS.ProcessEnv = process.env,
): Required<RenderOptions> {
  const noColor = Boolean(env.NO_COLOR && env.NO_COLOR.length > 0);
  // Many CI runners (`CI=1`) honor NO_COLOR automatically, but some don't.
  // We defer to NO_COLOR for color decisions and only use CI/non-TTY to
  // downgrade motion (see isInteractive).
  const asciiOnly =
    (env.LC_ALL ?? "").toUpperCase() === "C" ||
    (env.LANG ?? "").toUpperCase() === "C";
  return { noColor, asciiOnly };
}

/**
 * Whether the wizard should render motion (spinners, line-by-line stagger).
 * Returns false in CI, when stdout is not a TTY, or when reduced-motion
 * heuristics apply. CLI action code checks this before starting a spinner
 * and prints a plain status line instead.
 */
export function isInteractive(
  env: NodeJS.ProcessEnv = process.env,
  isTTY: boolean = Boolean((process.stdout as { isTTY?: boolean }).isTTY),
): boolean {
  if (!isTTY) return false;
  if (env.CI === "1" || env.CI === "true") return false;
  // GitHub Actions / Circle / etc. generally set CI; this is belt + suspenders.
  if (env.GITHUB_ACTIONS === "true") return false;
  return true;
}

const CSI = "\u001b[";

function wrap(code: string, text: string, enabled: boolean): string {
  if (!enabled) return text;
  return `${CSI}${code}m${text}${CSI}0m`;
}

export function colorize(
  text: string,
  ansi256: number,
  options: RenderOptions = {},
): string {
  return wrap(`38;5;${ansi256}`, text, !options.noColor);
}

export function bold(text: string, options: RenderOptions = {}): string {
  return wrap("1", text, !options.noColor);
}

export function dim(text: string, options: RenderOptions = {}): string {
  return wrap("2", text, !options.noColor);
}

/**
 * Format a confidence decimal with two digits (`0.87`). The design spec locks
 * this to the mono two-digit form; we never emit a bucket word.
 */
export function formatConfidence(value: number): string {
  const bounded = Math.max(0, Math.min(1, value));
  return bounded.toFixed(2);
}

/**
 * `→ Pace · Product   confidence 0.87`
 *
 * The agent name is colored via the ANSI 256 directory; the rest of the line
 * stays in default foreground so the eye anchors on the identity. Role comes
 * from AGENT_ROLES so copy is stable across CLI and web.
 */
export function renderRoutingLine(
  agent: AgentSlug,
  confidence: number,
  options: RenderOptions = {},
): string {
  const arrow = options.asciiOnly ? "->" : "→";
  const separator = options.asciiOnly ? " - " : " · ";
  const role = AGENT_ROLES[agent];
  const name = agent.charAt(0).toUpperCase() + agent.slice(1);
  const coloredName = colorize(name, AGENT_ANSI_256[agent], options);
  const confidenceLabel = dim("confidence", options);
  const confidenceValue = formatConfidence(confidence);
  return `  ${arrow} ${bold(coloredName, options)}${separator}${role}   ${confidenceLabel} ${confidenceValue}`;
}

/**
 * Derive the CLI confidence decimal from the server payload. The M-P2 API
 * returns a bucket (`low | med | high`); the spec requires a decimal on this
 * surface, so we map through a deterministic directory. When a later
 * milestone upgrades the classifier to emit a real score, the CLI can switch
 * to that number without changing the render layer.
 */
export function resolveConfidenceDecimal(
  plan: IntentPlanPreview,
): number {
  return CONFIDENCE_DECIMAL[plan.confidence];
}

/**
 * Render the box-drawn plan preview block.
 *
 * ┌─ Plan preview ─────────────────────┐
 * │ 1. Draft outreach positioning      │
 * │ 2. Build target list (50 accounts) │
 * │ 3. Send first wave this week       │
 * │                                    │
 * │ ETA under a week                   │
 * └────────────────────────────────────┘
 *
 * No drop shadow, no color fills, no rainbow borders — box-drawing only,
 * per the mediocrity risk call-outs in the spec ("over-ASCII-art trap").
 */
export function renderPlanPreview(
  plan: IntentPlanPreview,
  options: RenderOptions = {},
): string {
  const horizontal = options.asciiOnly ? "-" : "─";
  const topLeft = options.asciiOnly ? "+" : "┌";
  const topRight = options.asciiOnly ? "+" : "┐";
  const bottomLeft = options.asciiOnly ? "+" : "└";
  const bottomRight = options.asciiOnly ? "+" : "┘";
  const vertical = options.asciiOnly ? "|" : "│";

  const steps = plan.steps.map((step, index) => {
    const number = `${index + 1}.`.padEnd(3, " ");
    return `${number} ${step.title}`;
  });
  const etaLine = `ETA ${plan.eta}`;

  const lines = [...steps, "", etaLine];
  const label = "Plan preview";
  // Body row format is `│ <content> │` — content gets one space padding on
  // each side, so the inner-content column has width `contentWidth`.
  const contentWidth = Math.max(
    // Keep enough room for the top-chrome label plus its "─ " prefix and
    // a minimum trailing dash so the border visually wraps the label.
    label.length + 4,
    ...lines.map((line) => line.length),
  );

  // Total horizontal glyph count inside the chrome (matches bottom border).
  const innerWidth = contentWidth + 2; // leading + trailing space per body row
  // Top chrome: "┌─ Plan preview ─────────────┐" — same glyph count as bottom.
  const leadingDashes = horizontal.repeat(1);
  const trailingDashes = horizontal.repeat(
    Math.max(1, innerWidth - label.length - 3), // leading dash + 2 spaces
  );
  const top = `${topLeft}${leadingDashes} ${label} ${trailingDashes}${topRight}`;

  const body = lines
    .map((line) => {
      const padded = line.padEnd(contentWidth, " ");
      return `${vertical} ${padded} ${vertical}`;
    })
    .join("\n");

  const bottom = `${bottomLeft}${horizontal.repeat(innerWidth)}${bottomRight}`;

  return [top, body, bottom].join("\n");
}

/**
 * `>> [A]pprove   [E]dit in browser   [X]archive   [Q]uit`
 *
 * Single-letter hotkeys, mono-spaced, opinionated verb order. Approve is the
 * default. Colors mirror the web token directory: lime for the primary verb,
 * amber for adjust/edit, rose for archive, dim for quit.
 */
export interface HotkeyBarEntry {
  key: string;
  label: string;
  tone?: "primary" | "warning" | "danger" | "neutral";
}

const TONE_ANSI: Record<NonNullable<HotkeyBarEntry["tone"]>, number> = {
  primary: 190, // lime (--ox-primary)
  warning: 214, // amber (adjust)
  danger: 203, // rose (archive)
  neutral: 244, // bright black (quit)
};

export function renderHotkeyBar(
  entries: HotkeyBarEntry[],
  options: RenderOptions = {},
): string {
  const prefix = options.asciiOnly ? ">>" : ">>";
  const segments = entries.map((entry) => {
    const tone = entry.tone ?? "neutral";
    const keyLabel = `[${entry.key.toUpperCase()}]`;
    const coloredKey = colorize(keyLabel, TONE_ANSI[tone], options);
    return `${coloredKey}${entry.label}`;
  });
  return `${prefix} ${segments.join("   ")}`;
}

export const DEFAULT_HOTKEYS: HotkeyBarEntry[] = [
  { key: "a", label: "pprove", tone: "primary" },
  { key: "e", label: "dit in browser", tone: "warning" },
  { key: "x", label: "archive", tone: "danger" },
  { key: "q", label: "uit", tone: "neutral" },
];

/**
 * Normalize a keystroke to one of the hotkey verbs. Returns `null` for
 * ignored keys (CLI re-prompts instead of misinterpreting ambiguous input).
 */
export type IntentAction = "approve" | "edit" | "archive" | "quit";

export function normalizeHotkey(input: string): IntentAction | null {
  const trimmed = input.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  const ch = trimmed.charAt(0);
  switch (ch) {
    case "a":
      return "approve";
    case "e":
      return "edit";
    case "x":
      return "archive";
    case "q":
      return "quit";
    default:
      return null;
  }
}
